import {
  useWSModal,
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll,
  WSList,
  WSMenuItem,
  WSTableItem,
  WSTableToolbar
} from "@wingspanhq/fe-component-library";
import { InvoiceRow, InvoiceStatus } from "@wingspanhq/wingspan-api-sdk";
import { queriesInvoiceRows } from "@query";
import { useSession } from "@hooks/useSession";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Route,
  useHistory,
  useLocation,
  useRouteMatch
} from "react-router-dom";
import { InstantPayoutsModal } from "../../../../Invoices/screens/InstantPayoutsModal";
import { RouteInvoiceDetails } from "../../../../Invoices/screens/RouteInvoiceDetails";
import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import { openInNewTab } from "../../../../utils/openInNewTab";
import { useDownloadCsv } from "../../hooks/useDownloadCsv";
import { useBuildConfig } from "./filters/config";
import { createSearchRequest } from "./filters/request";
import { InvoiceFilter, InvoiceVisibleFilters } from "./filters/types";
import { ModalBulkAccept } from "./ModalBulkAccept";
import { ModalBulkDispute } from "./ModalBulkDispute";
import { TableInvoices } from "./TableInvoices";
import { useInvoiceActions } from "./useInvoiceAcitons";
import { selectorInvoicePermissions } from "../../../../shared/selectors/selectorInvoicePermissions";

export type InvoicesEmptyStateCopy = {
  firstTime?: string;
  standart: string;
};

type Props = {
  // The dashboard tab's status bucket; omit for the "all" tab.
  statuses?: InvoiceStatus[];
  // Gate fetching (e.g. only once a search term is entered).
  enabled?: boolean;
  visibleFilters?: InvoiceVisibleFilters;
  showDownloadCsv?: boolean;
  emptyState?: InvoicesEmptyStateCopy;
};

export const FilterableTableInvoices: React.FC<Props> = props => {
  const { urlQuery, setUrlQuery } = useUrlQuery<InvoiceFilter>();
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();

  const filtersConfig = useBuildConfig(props.visibleFilters);
  const modalBulkAccept = useWSModal(ModalBulkAccept);
  const modalBulkDispute = useWSModal(ModalBulkDispute);
  const getMenuActions = useInvoiceActions();
  const { downloadCsv, isLoading: isDownloadingCsv } = useDownloadCsv();

  const { accountId } = useSession();
  const [selection, setSelection] = useState<WSTableItem<InvoiceRow>[]>([]);
  const clearSelection = useCallback(() => setSelection([]), []);

  const reviewableSelection = useMemo(() => {
    const invoices = selection.map(item => item.data);
    return {
      accept: invoices.filter(
        invoice => selectorInvoicePermissions({ invoice, accountId }).canAccept
      ),
      dispute: invoices.filter(
        invoice => selectorInvoicePermissions({ invoice, accountId }).canDispute
      )
    };
  }, [selection, accountId]);

  // Clear selection whenever the tab/filters change so we never act on stale rows.
  useEffect(() => {
    setSelection([]);
  }, [urlQuery.filter, props.statuses]);

  const filter = useMemo(
    () => createSearchRequest(urlQuery.filter, props.statuses),
    [urlQuery.filter, props.statuses]
  );

  const query = queriesInvoiceRows.searchInvoiceRows.usePaginatedQuery(
    { filter },
    { enabled: props.enabled ?? true }
  );
  const invoices = query.flatData ?? [];
  const summary = query.data?.pages?.[0]?.summary;

  const hasUserFilters = useMemo(
    () => Object.keys(urlQuery.filter ?? {}).length > 0,
    [urlQuery.filter]
  );

  const onView = useCallback(
    (invoice: InvoiceRow) => {
      if (!invoice.id) {
        return;
      }
      history.push({
        pathname: `${location.pathname}/${invoice.id}`,
        search: location.search,
        state: { backPath: location.pathname }
      });
    },
    [history, location.pathname, location.search]
  );

  const onViewInNewTab = useCallback(
    (invoice: InvoiceRow) => {
      if (invoice.id) {
        openInNewTab(`${location.pathname}/${invoice.id}`);
      }
    },
    [location.pathname]
  );

  const rowMenuActions = useCallback(
    (invoice: InvoiceRow): WSMenuItem[] => [
      { icon: "search", label: "View", onClick: () => onView(invoice) },
      ...getMenuActions(invoice)
    ],
    [getMenuActions, onView]
  );

  const headerAction =
    props.showDownloadCsv && invoices.length > 0
      ? {
          icon: "download" as const,
          onClick: () => downloadCsv({ filter }),
          loading: isDownloadingCsv
        }
      : undefined;

  const renderEmptyState = () => {
    const showFirstTime = !hasUserFilters && !!props.emptyState?.firstTime;
    return (
      <WSEmptyState
        py="3XL"
        hasBorder
        bgColor="gray"
        type={hasUserFilters ? "search" : "documents"}
        title={hasUserFilters ? "No results found" : "No invoices"}
        description={
          showFirstTime
            ? props.emptyState?.firstTime
            : (props.emptyState?.standart ?? "No invoices found.")
        }
      />
    );
  };

  const renderContent = () => {
    if (query.isLoading) {
      return <TableInvoices loading invoices={[]} />;
    }

    if (query.isError) {
      return (
        <WSEmptyState
          py="3XL"
          hasBorder
          bgColor="gray"
          type="error"
          title="Something went wrong"
          description="We couldn't load your invoices."
          actions={{
            buttons: [{ label: "Try again", onClick: () => query.refetch() }]
          }}
        />
      );
    }

    if (invoices.length === 0) {
      return renderEmptyState();
    }

    return (
      <WSInfiniteScroll
        onLoad={() => query.fetchNextPage()}
        loadMore={invoices.length > 0}
        endOfList={!query.hasNextPage}
        loading={!!query.isFetchingNextPage}
      >
        <TableInvoices
          invoices={invoices}
          onRowClick={onView}
          onViewInNewTab={onViewInNewTab}
          selection={selection}
          onSelectionChange={setSelection}
          rowMenuActions={rowMenuActions}
          headerAction={headerAction}
        />
      </WSInfiniteScroll>
    );
  };

  return (
    <WSList gap="2XL">
      <Route
        path={`${match.path}/:invoiceId`}
        component={RouteInvoiceDetails}
        exact
      />
      <Route
        path={`${match.path}/:invoiceId/instant-deposit`}
        component={InstantPayoutsModal}
        exact
      />

      <WSFilters<InvoiceFilter>
        values={urlQuery.filter}
        onFilter={newValue =>
          setUrlQuery(prev => ({ ...prev, filter: newValue }))
        }
        primaryFilters={filtersConfig}
        info={
          summary
            ? { count: summary.count, amount: summary.amountTotal }
            : undefined
        }
      />

      {!query.isLoading && selection.length > 0 && (
        <WSTableToolbar
          count={selection.length}
          onClear={clearSelection}
          actionsLabel="Bulk actions"
          actions={[
            ...(reviewableSelection.dispute.length > 0
              ? [
                  {
                    label: "Dispute",
                    onClick: () =>
                      modalBulkDispute.open({
                        data: reviewableSelection.dispute,
                        onFinish: clearSelection
                      })
                  }
                ]
              : []),
            ...(reviewableSelection.accept.length > 0
              ? [
                  {
                    label: "Accept",
                    onClick: () =>
                      modalBulkAccept.open({
                        data: reviewableSelection.accept,
                        onFinish: clearSelection
                      })
                  }
                ]
              : [])
          ]}
        />
      )}

      {renderContent()}
    </WSList>
  );
};
